import type { AccountProps } from 'app-components/flux/accounts/accountsTypes';
import { useAppSelector } from 'hooks/redux';
import type { SyntheticEvent } from 'react';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import Stack from '@mui/material/Stack';

import { accountsSelectors } from 'app-components/flux/accounts';
import { businessSelectors } from 'app-components/flux/business';
import { categoriesSelectors } from 'app-components/flux/categories';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { createSelector } from 'reselect';

import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import CategoryField from 'components/QuickenControls/CategoryField';
import UsageSelect from 'components/UsageSelect';
import type { Map as ImmutableMap, List, RecordOf } from 'immutable';

export const getUsageSelector = createSelector(
  [
    categoriesSelectors.getCategoriesById,
    accountsSelectors.getAccountsById,
    businessSelectors.getDefaultBusiness,
    entitlementsSelectors.getBusinessEnabled,
    (_, props) => props,
  ],
  (categoriesById, accountsById, defaultBusiness, businessEnabled, props) => {
    const { accountId, coa, businessId } = props;
    if (!businessEnabled) {
      return 'PERSONAL';
    }
    if (coa?.id) {
      const category = categoriesById.get(coa.id);
      if (category) {
        const usageType = category.usageType || 'PERSONAL';
        if (usageType === 'BUSINESS') {
          if (businessId) return businessId;
          const account = accountId ? accountsById.get(accountId) : undefined;
          return account?.businessId || defaultBusiness?.id || 'PERSONAL';
        }
        return 'PERSONAL';
      }
    }

    if (businessId) {
      return businessId;
    }

    if (accountId) {
      const account = accountsById.get(accountId);
      if (account) {
        return account.usageType === 'BUSINESS' ? account.businessId || defaultBusiness?.id || 'PERSONAL' : 'PERSONAL';
      }
    }
    return 'PERSONAL';
  },
);

function UsageWithCategory({
  accountId,
  categoryProps,
  onChange,
  showCategoryOnly,
  showUsageOnly,
  usageProps,
  disabled,
  disabledPayment,
  /** MUI `small` — 48px row height to match UsageSelect + filled inputs */
  size,
}: {
  accountId?: string | null;
  categoryProps: any;
  onChange?: (props: { usage: string; coa: ChartOfAccountProps }) => void;
  showCategoryOnly?: boolean;
  showUsageOnly?: boolean;
  usageProps: {
    value: string | null | undefined;
    disabled?: boolean;
    iconOnly?: boolean;
    showPersonal?: boolean;
    className?: string;
    cellId?: string;
    size?: string;
  };
  disabled?: boolean | undefined;
  disabledPayment?: boolean;
  size?: 'small' | 'medium';
}) {
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const maxCategoryDepth = useAppSelector(
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'maxCategoryDepth') || 3,
  );
  const defaultBusiness = useSelector(businessSelectors.getDefaultBusiness);
  const accountsById: ImmutableMap<string, RecordOf<AccountProps>> = useSelector(accountsSelectors.getAccountsById);
  const arAccountIds: List<string | null | undefined> = useSelector(accountsSelectors.getAllArAccountIds);
  const categoriesById = useSelector(categoriesSelectors.getCategoriesById);
  const qbpAccountIds: List<string | null | undefined> = useSelector(accountsSelectors.getQBPAccountIds);

  const { value: usageValue } = usageProps;
  const { value: categoryFieldCoa, filterFn: categoryPropsFilterFn } = categoryProps;
  const paymentFilterFn = disabledPayment
    ? (coa: ChartOfAccountProps) => coa && !(coa.type === 'ACCOUNT' && arAccountIds.includes(coa.id))
    : undefined;
  const resolvedFilterFn =
    categoryPropsFilterFn && paymentFilterFn
      ? (coa: ChartOfAccountProps) => categoryPropsFilterFn(coa) && paymentFilterFn(coa)
      : categoryPropsFilterFn || paymentFilterFn;

  const getUsage = useCallback(
    // todo move to the selector above once this release is over and its proven to work o.O
    (coa: ChartOfAccountProps, businessId: string | null) => {
      let result;

      const category = coa?.id ? categoriesById.get(coa.id) : null;

      if (category) {
        const { usageType } = category;
        if (usageType === 'PERSONAL') {
          result = 'PERSONAL';
        } else if (usageType === 'BUSINESS' || businessId) {
          // BUSINESS category, or a neutral (unset usageType) category on a business txn:
          // keep the business context instead of forcing personal, so billable + project survive.
          result = businessId;
          if (!result) {
            const account = accountId ? accountsById.get(accountId) : undefined;
            result = account?.businessId || defaultBusiness?.id;
          }
        } else {
          result = 'PERSONAL';
        }
      } else if (businessId) {
        result = businessId;
      } else if (qbpAccountIds.includes(coa.id)) {
        const account = accountsById.get(coa.id);
        result = account?.businessId || defaultBusiness?.id;
      } else if (accountId) {
        const account = accountsById.get(accountId);
        if (account?.usageType === 'BUSINESS') {
          result = account?.businessId || defaultBusiness?.id;
        } else {
          result = 'PERSONAL';
        }
      } else {
        result = 'PERSONAL';
      }
      if (!result) {
        // likely only happens when there is no default business
        result = 'PERSONAL';
      }
      return result;
    },
    [accountId, accountsById, qbpAccountIds, categoriesById, defaultBusiness?.id],
  );

  const handleCategoryChange = (newCoa: ChartOfAccountProps) => {
    const newUsage = getUsage(newCoa, usageValue && usageValue !== 'PERSONAL' ? usageValue : null);
    onChange?.({ coa: newCoa, usage: newUsage });
  };

  // A stage-scoped `filterFn` (e.g. Bill Scout's Income/Bills category picker) can reject
  // Uncategorized outright — falling back to it here anyway would bypass the filter and let a
  // usage switch silently reassign the transaction to the other stage. Prefer a category of the
  // same type that matches the new usage instead; if none exists, leave the prior category rather
  // than assign one the filter would have hidden.
  const resolveUsageChangeCoa = (
    currentCoa: ChartOfAccountProps,
    categoryType: string | undefined,
    newUsageType: string,
  ): ChartOfAccountProps => {
    const uncategorizedCoa = { type: 'UNCATEGORIZED', id: '0' } as ChartOfAccountProps;
    if (!categoryPropsFilterFn || categoryPropsFilterFn(uncategorizedCoa)) {
      return uncategorizedCoa;
    }
    const matchingCategory = categoriesById.find(
      (candidate) => candidate.type === categoryType && (candidate.usageType || 'PERSONAL') === newUsageType,
    );
    return matchingCategory ? ({ type: 'CATEGORY', id: matchingCategory.id } as ChartOfAccountProps) : currentCoa;
  };

  const handleUsageChange = (_event: SyntheticEvent, newUsage: string) => {
    let newCoa = categoryFieldCoa;

    const category = categoryFieldCoa ? categoriesById.get(categoryFieldCoa.id) : null;
    if (category) {
      const usageType = category.usageType || 'PERSONAL';
      const newUsageType = newUsage === 'PERSONAL' ? 'PERSONAL' : 'BUSINESS';
      if (usageType !== newUsageType) {
        newCoa = resolveUsageChangeCoa(categoryFieldCoa, category.type, newUsageType);
      }
    } else {
      // If current category is a transfer to a business only account and usage is being changed to personal, then
      // we should change transfer to uncategorized
      //
      const isTrnToBusinessOnlyAccount = categoryFieldCoa && qbpAccountIds.includes(categoryFieldCoa.id);
      if (isTrnToBusinessOnlyAccount && newUsage === 'PERSONAL') {
        newCoa = resolveUsageChangeCoa(categoryFieldCoa, undefined, 'PERSONAL');
      }
    }

    onChange?.({ coa: newCoa, usage: newUsage });
  };

  const usageSelectSize = size ?? usageProps.size ?? 'medium';
  const categoryTextFieldProps = {
    ...categoryProps.textFieldProps,
    ...(size === 'small' ? { size: 'small' as const } : {}),
  };

  if (showCategoryOnly) {
    return <CategoryField {...categoryProps} textFieldProps={categoryTextFieldProps} onChange={handleCategoryChange} />;
  }
  if (showUsageOnly) {
    return <UsageSelect {...usageProps} size={usageSelectSize} onChange={handleUsageChange} />;
  }
  return (
    <Stack
      direction='row'
      sx={{
        '.MuiFormControl-root': {
          margin: 0,
        },
      }}
    >
      {businessEnabled && (
        <UsageSelect
          {...usageProps}
          size={usageSelectSize}
          onChange={handleUsageChange}
          slotProps={{
            input: {
              sx: {
                borderTopRightRadius: '0px',
                borderBottomRightRadius: '0px',
                borderRightStyle: 'none',
              },
            },
          }}
          disabled={disabled}
        />
      )}
      <CategoryField
        {...categoryProps}
        textFieldProps={categoryTextFieldProps}
        sx={
          businessEnabled
            ? {
                borderTopLeftRadius: '0px',
                borderBottomLeftRadius: '0px',
              }
            : {}
        }
        filterFn={resolvedFilterFn ?? undefined}
        onChange={handleCategoryChange}
        margin='normal'
        disabled={disabled}
        maxCategoryDepth={maxCategoryDepth}
      />
    </Stack>
  );
}

export default UsageWithCategory;
